// Normalization on every read and write (SPEC §4): prevents phantom diffs.
// - Line endings → LF (CRLF and lone CR).
// - Strip a leading UTF-8 BOM.
// - Keep the trailing-newline state exactly (we never add or remove a final newline).
// - Paths → Unicode NFC (macOS can return NFD names).

export function normalizeText(text: string): string {
  return text.replace(/^\uFEFF/, '').replace(/\r\n?/g, '\n');
}

export function normalizePath(path: string): string {
  return path.normalize('NFC');
}

const decoder = new TextDecoder('utf-8', { fatal: false });
const encoder = new TextEncoder();

/** Decode file bytes as UTF-8 and normalize. */
export function decodeText(bytes: Uint8Array): string {
  return normalizeText(decoder.decode(bytes));
}

export function encodeText(text: string): Uint8Array<ArrayBuffer> {
  return encoder.encode(normalizeText(text));
}

/** Only Markdown files are tracked (SPEC §4: read/write only .md + .hashquill/). */
export const isTrackedPath = (path: string) =>
  path.toLowerCase().endsWith('.md') && !path.split('/').some((seg) => seg === '.hashquill' || seg === '.git');

// ---------- Original encoding, restored on push ----------

/** How a file was stored on GitHub, so a push writes it back the same way. */
export type TextEncodingInfo = { eol: 'lf' | 'crlf'; bom: boolean };
export const DEFAULT_ENCODING: TextEncodingInfo = { eol: 'lf', bom: false };

/**
 * Read from the raw (un-normalized) text. Mixed endings count as CRLF when CRLF is at
 * least as common as lone LF: an edit can't reproduce a mix exactly, so the majority wins.
 */
export function detectEncoding(raw: string): TextEncodingInfo {
  const crlf = (raw.match(/\r\n/g) ?? []).length;
  const lf = (raw.match(/\n/g) ?? []).length - crlf;
  return { eol: crlf > 0 && crlf >= lf ? 'crlf' : 'lf', bom: raw.startsWith('\uFEFF') };
}

/** Normalized text → the bytes' text as GitHub had it (line endings and BOM). */
export function restoreEncoding(text: string, enc: TextEncodingInfo): string {
  const body = enc.eol === 'crlf' ? text.replace(/\n/g, '\r\n') : text;
  return enc.bom ? `\uFEFF${body}` : body;
}

/** Decode raw bytes without normalizing (to detect the original encoding). */
export function decodeRaw(bytes: Uint8Array): string {
  return new TextDecoder('utf-8', { fatal: false, ignoreBOM: true }).decode(bytes);
}
